<template>
  <div
    ref="triggerRef"
    class="tooltip-container"
    :class="{ 'tooltip-container-block': block }"
    @mouseenter="show"
    @mouseleave="hide"
    @click="onTriggerClick"
  >
    <slot></slot>
    <Teleport to="body">
      <div
        v-show="visible"
        ref="contentRef"
        class="tooltip-content tooltip-teleported"
        :class="{
          'tooltip-content-rich': hasPopoverContent,
          'tooltip-multiline': multiline,
          'tooltip-bottom': placement === 'bottom',
          visible: visible,
        }"
        :style="contentStyle"
        role="tooltip"
        @mouseenter="show"
        @mouseleave="hide"
      >
        <slot name="content">
          {{ content }}
        </slot>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, useSlots } from 'vue';

defineOptions({
  name: 'TooltipComponent',
});

const props = defineProps({
  content: {
    type: String,
    default: '',
  },
  /**
   * Stretch the trigger to the full cell width so truncate/ellipsis works in tables.
   */
  block: {
    type: Boolean,
    default: false,
  },
  /**
   * Wrap long help text instead of keeping it on one line.
   */
  multiline: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();
const hasPopoverContent = computed(() => Boolean(slots.content));

const triggerRef = ref(null);
const contentRef = ref(null);
const visible = ref(false);
const pinned = ref(false);
const placement = ref('top');
const contentStyle = ref({});
let hideTimeout = null;
let windowListenersBound = false;
let documentClickBound = false;
let documentClickToken = 0;

function clearHideTimeout() {
  if (hideTimeout) {
    clearTimeout(hideTimeout);
    hideTimeout = null;
  }
}

function bindWindowListeners() {
  if (windowListenersBound) {
    return;
  }
  windowListenersBound = true;
  window.addEventListener('scroll', hideImmediately, true);
  window.addEventListener('resize', hideImmediately);
  document.addEventListener('keydown', onKeydown);
}

function bindDocumentClick() {
  if (documentClickBound) {
    return;
  }
  documentClickBound = true;
  const token = ++documentClickToken;
  // Defer so the click that pins the tooltip does not immediately dismiss it.
  setTimeout(() => {
    if (token !== documentClickToken || !pinned.value) {
      if (token === documentClickToken) {
        documentClickBound = false;
      }
      return;
    }
    document.addEventListener('click', onDocumentClick);
  }, 0);
}

function unbindListeners() {
  documentClickToken += 1;
  if (windowListenersBound) {
    windowListenersBound = false;
    window.removeEventListener('scroll', hideImmediately, true);
    window.removeEventListener('resize', hideImmediately);
    document.removeEventListener('keydown', onKeydown);
  }
  if (documentClickBound) {
    documentClickBound = false;
    document.removeEventListener('click', onDocumentClick);
  }
}

async function show() {
  clearHideTimeout();
  visible.value = true;
  await nextTick();
  positionContent();
  // Re-measure after layout/wrapping so long text blocks get the correct height.
  requestAnimationFrame(() => positionContent());
  bindWindowListeners();
  if (pinned.value) {
    bindDocumentClick();
  }
}

function hide() {
  if (pinned.value) {
    return;
  }
  clearHideTimeout();
  // Small delay so the pointer can move onto the teleported tooltip without flicker.
  hideTimeout = setTimeout(() => {
    hideImmediately();
  }, 80);
}

function hideImmediately() {
  clearHideTimeout();
  visible.value = false;
  pinned.value = false;
  unbindListeners();
}

function onTriggerClick() {
  if (pinned.value) {
    hideImmediately();
    return;
  }
  pinned.value = true;
  show();
}

function onDocumentClick(event) {
  const target = event.target;
  if (triggerRef.value?.contains(target) || contentRef.value?.contains(target)) {
    return;
  }
  hideImmediately();
}

function onKeydown(event) {
  if (event.key === 'Escape') {
    hideImmediately();
  }
}

function positionContent() {
  const trigger = triggerRef.value;
  const content = contentRef.value;
  if (!trigger || !content) {
    return;
  }

  const rect = trigger.getBoundingClientRect();
  const contentRect = content.getBoundingClientRect();
  const gap = 8;
  const viewportPadding = 8;

  let top = rect.top - contentRect.height - gap;
  let nextPlacement = 'top';

  if (top < viewportPadding) {
    top = rect.bottom + gap;
    nextPlacement = 'bottom';
  }

  let left = rect.left + rect.width / 2 - contentRect.width / 2;
  left = Math.max(viewportPadding, Math.min(left, window.innerWidth - contentRect.width - viewportPadding));

  placement.value = nextPlacement;
  contentStyle.value = {
    position: 'fixed',
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    bottom: 'auto',
    right: 'auto',
    transform: 'none',
    margin: '0',
  };
}

onBeforeUnmount(() => {
  hideImmediately();
});
</script>
